import React, { useState } from 'react';

import { RadioGroup, VStack } from '@chakra-ui/react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';

import { Clone } from 'assets/assetsV1/iconComponents';

import { StringModifier, modifyString } from 'constants/copyHelpers';

import { ConfirmationModal } from 'uiToolkitV1/ConfirmationModal';
import { Radio } from 'uiToolkitV1/Radio';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

const CHILDREN_CLONING_OPTIONS = {
  NOT_CLONE_CHILDREN: 'NOT_CLONE_CHILDREN',
  CLONE_ONLY_CHILDREN_OBJECTIVES: 'CLONE_ONLY_CHILDREN_OBJECTIVES',
  CLONE_BOTH_CHILDREN_OBJECTIVES_AND_KRS: 'CLONE_BOTH_CHILDREN_OBJECTIVES_AND_KRS',
};

export const OKRCloneModal = ({
  isKR = true,
  isOpen,
  onClose,
  okrHasNoChildren = false,
  handleCloneNoChildren,
  handleCloneChildObjectives,
  handleCloneAllChildren,
}) => {
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();
  const [selectedChildrenOption, setSelectedChildrenOption] = useState(CHILDREN_CLONING_OPTIONS.NOT_CLONE_CHILDREN);

  const okrLabel = isKR ? keyResultsName : objectivesName;
  const okrLabelLowerCase = isKR
    ? modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
    : modifyString(objectivesName, StringModifier.SENTENCE_CASE);

  const BodyComp = okrHasNoChildren ? (
    <ModalBodyNoChildOKRs okrLabel={okrLabelLowerCase} />
  ) : (
    <ModalBody
      okrLabel={okrLabelLowerCase}
      selectedChildrenOption={selectedChildrenOption}
      setSelectedChildrenOption={setSelectedChildrenOption}
    />
  );

  const handleCloningConfirmation = () => {
    if (selectedChildrenOption === CHILDREN_CLONING_OPTIONS.NOT_CLONE_CHILDREN) {
      handleCloneNoChildren();
      return;
    }

    if (selectedChildrenOption === CHILDREN_CLONING_OPTIONS.CLONE_ONLY_CHILDREN_OBJECTIVES) {
      handleCloneChildObjectives();
      return;
    }

    if (selectedChildrenOption === CHILDREN_CLONING_OPTIONS.CLONE_BOTH_CHILDREN_OBJECTIVES_AND_KRS) {
      handleCloneAllChildren();
    }
  };

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      title={
        <>
          <Clone style={{ marginRight: '4px' }} />
          <span>Cloning {okrLabel}</span>
        </>
      }
      body={BodyComp}
      cancelText="Cancel"
      onCancel={onClose}
      continueText={okrHasNoChildren ? 'Continue' : 'Confirm'}
      onContinue={handleCloningConfirmation}
    />
  );
};

const ModalBody = ({ okrLabel, selectedChildrenOption, setSelectedChildrenOption }) => {
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();
  return (
    <>
      <TextBodyMedium>
        Cloning this {okrLabel} will automatically create an exact copy of it. Are you sure you want to continue?
      </TextBodyMedium>
      <RadioGroup onChange={setSelectedChildrenOption} value={selectedChildrenOption}>
        <VStack py={2.5} px={3} alignItems="flex-start">
          <Radio
            isChecked={selectedChildrenOption === CHILDREN_CLONING_OPTIONS.NOT_CLONE_CHILDREN}
            value={CHILDREN_CLONING_OPTIONS.NOT_CLONE_CHILDREN}
          >
            <TextBodyMedium>
              I don’t want to clone any child {modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}
            </TextBodyMedium>
          </Radio>
          <Radio
            isChecked={selectedChildrenOption === CHILDREN_CLONING_OPTIONS.CLONE_ONLY_CHILDREN_OBJECTIVES}
            value={CHILDREN_CLONING_OPTIONS.CLONE_ONLY_CHILDREN_OBJECTIVES}
          >
            <TextBodyMedium>
              I want to clone only child {modifyString(objectivesName, StringModifier.PLURAL_SENTENCE_CASE)}
            </TextBodyMedium>
          </Radio>
          <Radio
            isChecked={selectedChildrenOption === CHILDREN_CLONING_OPTIONS.CLONE_BOTH_CHILDREN_OBJECTIVES_AND_KRS}
            value={CHILDREN_CLONING_OPTIONS.CLONE_BOTH_CHILDREN_OBJECTIVES_AND_KRS}
          >
            <TextBodyMedium>
              I want to clone both child {modifyString(objectivesName, StringModifier.PLURAL_SENTENCE_CASE)} and{' '}
              {modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}
            </TextBodyMedium>
          </Radio>
        </VStack>
      </RadioGroup>
    </>
  );
};

const ModalBodyNoChildOKRs = ({ okrLabel }) => (
  <TextBodyMedium>
    Cloning this {okrLabel} will automatically create an exact copy of it. Are you sure you want to continue?
  </TextBodyMedium>
);
